import type { AuthServiceApi } from "~/environment/auth.service";
import {
  RecordMap,
  RecordPointer,
  assignToRecordMap,
  createRecordMapFromPointersWithRecords,
  getMapRecord,
  getMapRecords,
  getPointer,
} from "libs/schema";
import { UnreachableCaseError } from "libs/errors";
import { ClientRecordLoaderApi, GetOptions } from "~/environment/RecordLoader";

export interface GetAllThreadViewDataProps {
  threadId: string;
  getParentThreads?: "none" | "first" | "all";
}

export type GetAllThreadViewDataResult = {
  recordMap: RecordMap;
};

export type GetAllThreadViewDataRecordLoader = Pick<
  ClientRecordLoaderApi,
  | "getRecords"
  | "getThreadTags"
  | "getThreadGroupPermissions"
  | "getThreadUserParticipants"
  | "getThreadUserPermissions"
  | "getThreadTimelineEntries"
  | "getDrafts"
  | "getThreadSubscriptions"
  | "getThreadRequests"
>;

export async function getAllThreadViewData(
  environment: {
    auth: AuthServiceApi;
    recordLoader: GetAllThreadViewDataRecordLoader;
  },
  props: GetAllThreadViewDataProps,
  options?: GetOptions,
): Promise<GetAllThreadViewDataResult> {
  const { recordLoader, auth } = environment;
  const { threadId, getParentThreads = "none" } = props;
  const currentUserId = auth.getAndAssertCurrentUserId();

  const pointers = [
    getPointer("thread", threadId),
    getPointer("notification", {
      thread_id: threadId,
      user_id: currentUserId,
    }),
    getPointer("thread_read_receipt", {
      thread_id: threadId,
      user_id: currentUserId,
    }),
    getPointer("thread_seen_receipt", {
      thread_id: threadId,
      user_id: currentUserId,
    }),
  ];

  const queries = await Promise.all([
    recordLoader.getRecords(pointers, options).then(([pointersWithRecords]) => {
      const recordMap = createRecordMapFromPointersWithRecords(pointersWithRecords);

      return [undefined, { recordMap }] as const;
    }),
    recordLoader.getThreadTags({ thread_id: threadId }, options),
    recordLoader.getThreadGroupPermissions({ thread_id: threadId }, options),
    recordLoader.getThreadUserParticipants({ thread_id: threadId }, options),
    recordLoader.getThreadUserPermissions({ thread_id: threadId }, options),
    recordLoader.getThreadTimelineEntries({ thread_id: threadId }, options),
    recordLoader.getDrafts({ threadId, currentUserId: currentUserId }, options),
    recordLoader.getThreadSubscriptions({ thread_id: threadId }, options),
    recordLoader.getThreadRequests({ thread_id: threadId }, options),
  ]);

  const recordMaps = queries.map(([, { recordMap }]) => recordMap) as [RecordMap, ...RecordMap[]];

  const recordMap: RecordMap = {};

  assignToRecordMap(recordMap, ...recordMaps);

  const threadTags = getMapRecords(recordMap, "thread_tag");
  const groupPermissions = getMapRecords(recordMap, "thread_group_permission");
  const userPermissions = getMapRecords(recordMap, "thread_user_permission");
  const userParticipants = getMapRecords(recordMap, "thread_user_participant");
  const threadSubscriptions = getMapRecords(recordMap, "thread_subscription");
  const timelineEntries = getMapRecords(recordMap, "thread_timeline");

  const additionalPointers = [
    ...threadTags.map((r) => getPointer("tag", r.tag_id)),
    ...threadTags.map((r) =>
      getPointer("tag_subscription", {
        tag_id: r.tag_id,
        user_id: currentUserId,
      }),
    ),
    ...groupPermissions.map((r) => getPointer("tag", r.group_id)),
    ...groupPermissions.map((r) =>
      getPointer("tag_subscription", {
        tag_id: r.group_id,
        user_id: currentUserId,
      }),
    ),
    ...userPermissions.map((r) => getPointer("user_profile", r.user_id)),
    ...userParticipants.map((r) => getPointer("user_profile", r.user_id)),
    ...threadSubscriptions.map((r) => getPointer("user_profile", r.user_id)),
    ...timelineEntries.flatMap((r): RecordPointer[] => {
      switch (r.type) {
        case "BRANCHED_DRAFT": {
          return [getPointer("draft", r.entry_id)];
        }
        case "BRANCHED_THREAD": {
          return [getPointer("thread", r.entry_id)];
        }
        case "MESSAGE": {
          return [getPointer("message", r.entry_id), getPointer("message_reactions", r.entry_id)];
        }
        default: {
          throw new UnreachableCaseError(r.type);
        }
      }
    }),
  ];

  const [additionalPointerWithRecords] = await recordLoader.getRecords(additionalPointers, options);

  assignToRecordMap(recordMap, additionalPointerWithRecords);

  const thread = getMapRecord(recordMap, {
    table: "thread",
    id: threadId,
  });

  if (thread?.branched_from_thread_id) {
    switch (getParentThreads) {
      case "none": {
        break;
      }
      case "first":
      case "all": {
        const { recordMap: parentRecordMap } = await getAllThreadViewData(
          environment,
          {
            threadId: thread.branched_from_thread_id,
            getParentThreads: getParentThreads === "first" ? "none" : "all",
          },
          options,
        );

        assignToRecordMap(recordMap, parentRecordMap);
        break;
      }
      default: {
        throw new UnreachableCaseError(getParentThreads);
      }
    }
  }

  return { recordMap };
}
